/**
 * Calendar date helpers for YYYY-MM-DD fields (e.g. DOB).
 * Avoid `new Date("YYYY-MM-DD")` and `toISOString().slice(0, 10)` — they use UTC
 * and shift the calendar day in US timezones.
 */

/**
 * Parse YYYY-MM-DD as a local calendar date.
 * Invalid calendar dates (e.g. 2026-02-31) return null. Never uses `new Date("YYYY-MM-DD")`.
 */
export function parseDateOnlyLocal(isoDate: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate.trim());
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const day = Number(m[3]);
  const d = new Date(y, mo - 1, day);
  if (d.getFullYear() !== y || d.getMonth() !== mo - 1 || d.getDate() !== day) {
    return null;
  }
  return d;
}

/** Normalize Mantine DatePicker / range picker values without UTC day shift. */
export function calendarDateFromPickerValue(v: string | Date | null | undefined): Date | null {
  if (v == null) return null;
  if (typeof v === 'string') {
    const t = v.trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(t)) {
      return parseDateOnlyLocal(t.slice(0, 10));
    }
    const parsed = new Date(t);
    if (Number.isNaN(parsed.getTime())) return null;
    return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
  }
  if (Number.isNaN(v.getTime())) return null;
  return new Date(v.getFullYear(), v.getMonth(), v.getDate());
}

export type CalendarDateRange = [Date | null, Date | null];

/** Normalize both ends of a Mantine range picker value. */
export function calendarDateRangeFromPickerValue(
  range: [string | Date | null, string | Date | null],
): CalendarDateRange {
  return [
    calendarDateFromPickerValue(range[0]),
    calendarDateFromPickerValue(range[1]),
  ];
}

/** Format a calendar Date as MM/DD/YYYY using local date components. */
export function formatCalendarDateUs(d: Date): string {
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${mo}/${day}/${d.getFullYear()}`;
}

/** Parse MM/DD/YYYY as a local calendar date. */
export function parseCalendarDateUs(text: string): Date | null {
  const match = text.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return null;
  const month = Number(match[1]);
  const day = Number(match[2]);
  const year = Number(match[3]);
  const d = new Date(year, month - 1, day);
  if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) {
    return null;
  }
  return d;
}

/** Normalize Date | null range values (e.g. from controlled state). */
export function normalizeCalendarDateRange(range: CalendarDateRange): CalendarDateRange {
  return [
    range[0] ? calendarDateFromPickerValue(range[0]) : null,
    range[1] ? calendarDateFromPickerValue(range[1]) : null,
  ];
}

/** Format a Date as YYYY-MM-DD using local date components. */
export function formatDateOnlyLocal(d: Date): string {
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${mo}-${day}`;
}

/** Normalize DateInput value to stored YYYY-MM-DD. */
export function dateInputValueToYyyyMmDd(v: Date | string | null): string {
  if (v == null) return '';
  if (typeof v === 'string') {
    const t = v.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t;
    const parsed = new Date(t);
    return Number.isNaN(parsed.getTime()) ? '' : formatDateOnlyLocal(parsed);
  }
  return formatDateOnlyLocal(v);
}

function normalizeWallTimeHhmm(timeHhmm: string): string {
  const t = timeHhmm.trim();
  return t.length === 5 ? `${t}:00` : t;
}

/** Apply HH:mm[:ss] wall time to a YYYY-MM-DD calendar date (local components). */
export function calendarDateWithWallTime(dateKey: string, timeHhmm: string): Date {
  const d = parseDateOnlyLocal(dateKey);
  if (!d) {
    throw new Error(`Invalid calendar date: ${dateKey}`);
  }
  const [h, min] = normalizeWallTimeHhmm(timeHhmm).split(':').map(Number);
  d.setHours(h, min, 0, 0);
  return d;
}

/** Friendly label for a picked booking date + wall time (no UTC day shift). */
export function formatBookingWallDateTimeLabel(dateKey: string, timeHhmm: string): string {
  const d = calendarDateWithWallTime(dateKey, timeHhmm);
  return d.toLocaleString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** Friendly label for an ISO instant in a specific IANA time zone (provider slot times). */
export function formatInstantInTimeZone(iso: string, timeZone: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone,
  });
}

/** Date + start–end range for ISO instants in a specific IANA time zone. */
export function formatInstantRangeInTimeZone(
  isoStart: string,
  isoEnd: string,
  timeZone: string,
): string {
  const start = new Date(isoStart);
  const end = new Date(isoEnd);
  if (Number.isNaN(start.getTime())) return isoStart;
  if (Number.isNaN(end.getTime())) return formatInstantInTimeZone(isoStart, timeZone);

  const datePart = start.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    timeZone,
  });
  const tf = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone,
  });
  return `${datePart} · ${tf.format(start)} – ${tf.format(end)}`;
}

/** Date + start–end range for a picked booking date + wall time. */
export function formatBookingWallDateTimeRangeLabel(
  dateKey: string,
  timeHhmm: string,
  durationMinutes: number,
): string {
  const start = calendarDateWithWallTime(dateKey, timeHhmm);
  const end = new Date(start.getTime() + durationMinutes * 60_000);
  const datePart = start.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
  const tf = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${datePart} · ${tf.format(start)} – ${tf.format(end)}`;
}

/** ISO instant from calendar date + wall time using local date components. */
export function bookingWallDateTimeToIso(dateKey: string, timeHhmm: string): string {
  return calendarDateWithWallTime(dateKey, timeHhmm).toISOString();
}
